import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  useTheme,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  IconButton,
  DialogContent,
  Divider,
} from "@mui/material";
import {
  HelpOutline as HelpOutlineIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import { ReactComponent as ProvideDataIcon } from "../../../../images/qe-strategy/ProvideIcon.svg";
import { ReactComponent as AIAnalysisIcon } from "../../../../images/qe-strategy/AIAnalysisIcon.svg";
import { ReactComponent as ReviewInsightsIcon } from "../../../../images/qe-strategy/ReviewInsightsIcon.svg";
import { ReactComponent as QuesttionaireIcon } from "../../../../images/qe-strategy/QuesttionaireIcon.svg";
import { ReactComponent as WebsiteIcon } from "../../../../images/qe-strategy/WebsiteIcon.svg";
import { ReactComponent as UploadIconQES } from "../../../../images/qe-strategy/UploadIconQES.svg";
import { ReactComponent as BenchmarksIcon } from "../../../../images/qe-strategy/BenchmarksIcon.svg";
import { ReactComponent as IdentifyGapsIcon } from "../../../../images/qe-strategy/IdentifyGapsIcon.svg";
import { ReactComponent as PrioritiseStrengthIcon } from "../../../../images/qe-strategy/PrioritiseStrengthIcon.svg";
import VectorIcon from "../../../../images/Vector.svg";
import Upload from "../../../../images/Upload.svg";
import WebsiteIconQES from "../../../../images/WebsiteIcon.svg";
import SmartImportIcon from "../../../../images/SmartImportIcon.svg";

const EnterpriseTour = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleNext = () => setStep((prev) => Math.min(prev + 1, 2));
  const handlePrevious = () => setStep((prev) => Math.max(prev - 1, 0));

  const handleSkip = () => {
    if (dontShowAgain) sessionStorage.setItem("hideEnterpriseTour", "true");
    onClose();
  };

  const handleGetStarted = () => {
    if (dontShowAgain) sessionStorage.setItem("hideEnterpriseTour", "true");
    onClose();
  };

  const tourData = [
    {
      title: "What is Enterprise QE Strategy?",
      subtitle:
        "A comprehensive AI-powered framework designed for executive leaders to assess and transform quality engineering maturity",
      cards: [
        {
          icon: <ProvideDataIcon />,
          title: "Provide Data",
          desc: "Start with a questionnaire, upload docs, or share your portal",
        },
        {
          icon: <AIAnalysisIcon />,
          title: "AI Analysis",
          desc: "AI that finds gaps and benchmarks your performance",
        },
        {
          icon: <ReviewInsightsIcon />,
          title: "Review Insights",
          desc: "Insights, benchmarks, and roadmaps—all in your dashboard",
        },
      ],
    },
    {
      title: "How it works",
      subtitle:
        "Simple steps to get comprehensive quality insights for your organization",
      cards: [
        {
          icon: <QuesttionaireIcon />,
          title: "Questionnaire",
          desc: "Fill in a complete guided questionnaire",
        },
        {
          icon: <UploadIconQES />,
          title: "Upload & Analyse",
          desc: "Upload existing documentation",
        },
        {
          icon: <WebsiteIcon />,
          title: "Website Analysis",
          desc: "Provide your application's front end or documentation link",
        },
      ],
    },
    {
      title: "What You'll Achieve",
      subtitle:
        "Transform your quality strategy with actionable insights and measurable outcomes",
      cards: [
        {
          icon: <PrioritiseStrengthIcon />,
          title: "Prioritize Strengths",
          desc: "Recognize areas of excellence to maintain and leverage",
        },
        {
          icon: <IdentifyGapsIcon />,
          title: "Identify Gaps",
          desc: "AI-ranked opportunities based on impact and feasibility",
        },
        {
          icon: <BenchmarksIcon />,
          title: "Industry Benchmarks",
          desc: "Compare with industry standards for each quality domain",
        },
      ],
    },
  ];

  const current = tourData[step];

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      onClose={handleSkip}
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 0,
          maxWidth: 740,
        },
      }}
    >
      {/* Header with close button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          p: 2,
          pb: 0,
        }}
      >
        <IconButton
          onClick={handleSkip}
          sx={{
            color: "#666",
            "&:hover": { backgroundColor: "#f5f5f5" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 4, py: 2 }}>
        {/* Title and Subtitle */}
        <Box textAlign="center" mb={4}>
          <Typography
            variant="h4"
            fontWeight={600}
            gutterBottom
            sx={{
              fontSize: "1.75rem",
              color: "#1a1a1a",
              mb: 2,
            }}
          >
            {current.title}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: 400,
              mx: "auto",
              lineHeight: 1.6,
              fontSize: "0.95rem",
              color: "#666",
            }}
          >
            {current.subtitle}
          </Typography>
        </Box>

        {/* Cards */}
        <Grid
          container
          spacing={3}
          justifyContent="center"
          alignItems="stretch" //  makes all cards equal height
          mb={4}
        >
          {current.cards.map((card, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={index}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: "100%", //  Full width of Grid cell
                  maxWidth: 220, //  Keeps consistent width (optional, tweak if needed)
                  textAlign: "center",
                  borderRadius: 2,
                  p: 3,
                  flexGrow: 1, //  Expands to fill vertical space equally
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  background: "#F3F8FF",
                  boxSizing: "border-box", //  Ensures consistent box model
                }}
              >
                <Box
                  sx={{
                    fontSize: 32,
                    mb: 1.5,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    "& svg": { fontSize: 40, width: 40, height: 40 }, // normalize SVG icon size
                  }}
                >
                  {card.icon}
                </Box>
                <Typography
                  fontWeight={600}
                  sx={{
                    mb: 1,
                    fontSize: "1rem",
                    color: "#1a1a1a",
                  }}
                >
                  {card.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    fontSize: "0.875rem",
                    lineHeight: 1.4,
                    maxWidth: 180,
                  }}
                >
                  {card.desc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Bottom Section */}
        {/* Bottom Section */}
        <Box sx={{ px: 2, pb: 2 }}>
          {/* Actions - Stepper and Buttons aligned on one line */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              position: "relative",
              minHeight: 56,
            }}
          >
            {/* Left side */}
            <Box>
              {step === 0 ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dontShowAgain}
                      onChange={(e) => setDontShowAgain(e.target.checked)}
                      sx={{
                        color: "#666",
                        "&.Mui-checked": { color: "#1976d2" },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "0.875rem", color: "#666" }}>
                      Don't show again
                    </Typography>
                  }
                />
              ) : (
                <Button
                  variant="outlined"
                  onClick={handlePrevious}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    fontWeight: 500,
                  }}
                >
                  Previous
                </Button>
              )}
            </Box>

            {/* Center stepper */}
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {[0, 1, 2].map((index) => (
                <Box
                  key={index}
                  sx={{
                    width: index === step ? 24 : 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: index === step ? "#0094FB" : "#e0e0e0",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
              <Typography
                variant="body2"
                sx={{
                  ml: 2,
                  color: "#666",
                  fontSize: "0.875rem",
                }}
              >
                {step + 1} / 3
              </Typography>
            </Box>

            {/* Right side */}
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                onClick={handleSkip}
                sx={{
                  textTransform: "none",
                  color: "#1976d2",
                  fontWeight: 500,
                }}
              >
                Skip Tour
              </Button>

              {step < 2 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    px: 3,
                    fontWeight: 500,
                    background: "#0094FB",
                  }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleGetStarted}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    px: 3,
                    fontWeight: 500,
                    background: "#0094FB",
                  }}
                >
                  Get Started
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const EnterpriseQEStrategy = () => {
  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [showTour, setShowTour] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const hideTour = sessionStorage.getItem("hideEnterpriseTour");
    if (!hideTour) {
      setShowTour(true);
    }
  }, []);

  return (
    <>
      <EnterpriseTour open={showTour} onClose={() => setShowTour(false)} />
      <Box sx={{ minHeight: "100vh", py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 400,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                mb: 2,
                color: "#1a1a1a",
              }}
            >
              Enterprise QE Strategy
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#333",
                fontSize: { xs: "0.95rem", md: "1rem" },
                lineHeight: 1.6,
              }}
            >
              Upload files, analyze content, and streamline
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#333",
                fontSize: { xs: "0.95rem", md: "1rem" },
                lineHeight: 1.6,
              }}
            >
              your quality engineering strategy
            </Typography>
          </Box>

          <Divider
            sx={{ borderColor: "#bdbdbd", borderWidth: "1.5px", mb: 6 }}
          />

          {/* Guided Strategy Questionnaire */}
          <Box sx={{ mb: 3 }}>
            <Card
              elevation={0}
              onClick={() => navigate("/dashboard/qe-strategy")}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                transition: "box-shadow 0.3s ease",
                cursor: "pointer", //  cursor shows it's clickable
                "&:hover": {
                  backgroundColor: "#f9f9f9", //  light grey background on hover
                },
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box
                    component="img"
                    src={VectorIcon}
                    alt="Guided Strategy Icon"
                    sx={{ mr: 1.5, width: 28, height: 28 }}
                  />
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      fontWeight: 500,
                      color: "#2196f3",
                      fontSize: { xs: "1.1rem", md: "1.2rem" },
                      textAlign: "center",
                    }}
                  >
                    Guided Strategy Questionnaire
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    lineHeight: 1.7,
                    fontSize: { xs: "0.875rem", md: "0.9rem" },
                    textAlign: "center",
                  }}
                >
                  This step-by-step assessment ensures we capture the full
                  context of your application's business criticality,
                  architecture, and quality maturity. It is designed by industry
                  experts to uncover risks and opportunities that automated
                  scans often miss.
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Upload Documentation & Website Analysis */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                onClick={() =>
                  navigate("/dashboard/qe-strategy/upload-documents")
                }
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  height: "100%",
                  transition: "box-shadow 0.3s ease",
                  cursor: "pointer", //  cursor shows it's clickable
                  "&:hover": {
                    backgroundColor: "#f9f9f9", //  light grey background on hover
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={Upload}
                      alt="Upload Documentation Icon"
                      sx={{ mr: 1.5, width: 28, height: 28 }}
                    />
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 500,
                        color: "#2196f3",
                        fontSize: { xs: "1.1rem", md: "1.2rem" },
                        textAlign: "center",
                      }}
                    >
                      Upload Documentation
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      lineHeight: 1.7,
                      fontSize: { xs: "0.875rem", md: "0.9rem" },
                      textAlign: "center",
                    }}
                  >
                    Leverage existing system design docs, architecture reviews,
                    or strategy presentations. Our AI will parse the documents
                    to pre-fill the assessment, saving you time on data entry.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                onClick={() =>
                  navigate("/dashboard/qe-strategy/website-analysis")
                }
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  height: "100%",
                  transition: "box-shadow 0.3s ease",
                  cursor: "pointer", //  cursor shows it's clickable
                  "&:hover": {
                    backgroundColor: "#f9f9f9", //  light grey background on hover
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={WebsiteIconQES}
                      alt="Website Icon"
                      sx={{ mr: 1.5, width: 28, height: 28 }}
                    />
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 500,
                        color: "#2196f3",
                        fontSize: { xs: "1.1rem", md: "1.2rem" },
                        textAlign: "center",
                      }}
                    >
                      Website Analysis
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      lineHeight: 1.7,
                      fontSize: { xs: "0.875rem", md: "0.9rem" },
                      textAlign: "center",
                    }}
                  >
                    Provide a link to your application's front-end,
                    documentation portal, or repository. This method focuses on
                    extracting technical signals to complement the strategic
                    context.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Clean Tip Section - matches card alignment */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                width: "100%",
                maxWidth: 680,
                px: { xs: 2, sm: 3 },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: "2px",
                }}
              >
                <Box
                  component="img"
                  src={SmartImportIcon}
                  alt="Tip Icon"
                  sx={{ width: 18, height: 18 }}
                />
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "#000",
                    fontSize: "0.9rem",
                    mb: 0.5,
                    lineHeight: 1.3,
                  }}
                >
                  Here is a tip for you
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#555",
                    fontSize: "0.85rem",
                    lineHeight: 1.5,
                  }}
                >
                  Most users begin with the guided questionnaire, but you can
                  also switch to document import or website analysis for a
                  faster setup.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Centered Need Help link */}
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Typography
              component="a"
              href="#"
              sx={{
                color: "#2196f3",
                fontSize: "0.8rem",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Need Help
              <HelpOutlineIcon sx={{ fontSize: 16 }} />
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default EnterpriseQEStrategy;
